// Shared suppression logic for the Family Worship promo pop-ups (slide-in +
// exit-intent). A pop-up must NOT appear if the visitor has either:
//   1. dismissed it in the past (persisted across sessions via localStorage), or
//   2. already signed up for either campaign.
//
// Dismissals were previously kept in sessionStorage, which only lasts the
// current tab/session — so "dismissed in the past" didn't actually hold across
// visits. These helpers use localStorage so the choice sticks.

// Signup is recorded by the campaign signup flows:
//   - localStorage 'rfw-signed-up' (Restore Family Worship)
//   - localStorage 'wt-signed-up'  (Worship Together)
//   - 'fwKitConverted' is set when a visitor clicks a kit CTA (treated as a
//     soft conversion — they're already in the funnel, so stop nagging).
const SIGNUP_KEYS = ['rfw-signed-up', 'wt-signed-up']

export function hasSignedUp() {
	if (typeof window === 'undefined') return false
	try {
		for (const k of SIGNUP_KEYS) {
			if (window.localStorage.getItem(k) === '1') return true
		}
		// fwKitConverted is written to both storages by the CTA click handlers.
		if (window.localStorage.getItem('fwKitConverted') === '1') return true
		if (window.sessionStorage.getItem('fwKitConverted') === '1') return true
	} catch {}
	return false
}

export function wasDismissed(key) {
	if (typeof window === 'undefined' || !key) return false
	try {
		// Honor both the new persistent flag and any legacy session flag so a
		// visitor who dismissed earlier this session isn't re-prompted.
		if (window.localStorage.getItem(key) === '1') return true
		if (window.sessionStorage.getItem(key) === '1') return true
	} catch {}
	return false
}

export function markDismissed(key) {
	if (typeof window === 'undefined' || !key) return
	try {
		window.localStorage.setItem(key, '1')
		window.sessionStorage.setItem(key, '1')
	} catch {}
}

// True when the pop-up should be suppressed outright (dismissed before OR
// already signed up). `key` is the element's persistent dismiss key.
export function shouldSuppressPopup(key) {
	return popupTakeoverActive() || hasSignedUp() || wasDismissed(key)
}

// HAP-5308: a site-wide takeover popup (e.g. /js/myp-popup.js, "Man Your Post",
// through 2026-11-18) sets window.CFL_POPUP_TAKEOVER while its campaign runs.
// Every other promo popup stands down until it clears (endsAt passes).
export function popupTakeoverActive() {
	return typeof window !== 'undefined' && !!window.CFL_POPUP_TAKEOVER
}

// Only ONE promo pop-up (slide-in, exit-intent, or the book-offer modal) should
// ever be visible in a single session — whichever arms first wins. This keeps a
// new campaign pop-up from stacking on top of / colliding with the existing
// Family Worship pop-ups. The flag is session-scoped (sessionStorage): it resets
// next visit so a different pop-up can win another day.
const POPUP_ACTIVE_KEY = 'cflPromoPopupShown'

export function anyPromoPopupShown() {
	if (typeof window === 'undefined') return false
	try {
		return window.sessionStorage.getItem(POPUP_ACTIVE_KEY) === '1'
	} catch {
		return false
	}
}

export function markPromoPopupShown() {
	if (typeof window === 'undefined') return
	try {
		window.sessionStorage.setItem(POPUP_ACTIVE_KEY, '1')
	} catch {}
}

// Embed pages (e.g. /sermons/embed/church/:id, /directory/embed/church/:id) are
// designed to be dropped into a partner church's own website via an <iframe>.
// They must NEVER render CFL's own promotional campaign content — announcement
// bars, Restore Family Worship / Family Worship pop-ups, banners, etc. — because
// that content belongs on churchandfamilylife.com, not on someone else's site.
// Detect them by path so every promo element (all of which share
// campaignElementMixin) can suppress itself in one place.
export function isEmbedRoute(path) {
	if (typeof path !== 'string') {
		if (typeof window === 'undefined') return false
		path = window.location.pathname
	}
	return /\/embed(\/|$)/i.test(path)
}

// The re-engagement confirmation page (`/stay`) is the destination for the
// "Keep Me on the List" button sent to lapsed subscribers. It has to be one
// screen with exactly one message — a promo pop-up or announcement bar asking
// them for something else undercuts the confirmation they just clicked for.
// (HAP-3172)
// The pastor landing (`/worship-together/pastors`) is cold traffic arriving from
// a cold email with exactly one ask — request the Church Pack. The site-wide
// "Free Family Worship starter kit" announcement bar is a competing, parent-facing
// CTA there, and the campaign page it promotes already hides it. (HAP-3573)
// The donation page (`/donate`) is the same case, and worse: measured at 1440px on
// the live page, the starter-kit slide-in rendered directly over the donation
// form's Email field. A promo asking for an email address cannot sit on top of the
// one form on the site that is asking for money. (HAP-4046)
const PROMO_FREE_PATHS = [/^\/stay(\/|$)/i, /^\/worship-together\/pastors(\/|$)/i, /^\/donate(\/|$)/i]

// Routes where no promotional campaign element may render, for any reason.
export function isPromoFreeRoute(path) {
	if (typeof path !== 'string') {
		if (typeof window === 'undefined') return false
		path = window.location.pathname
	}
	return isEmbedRoute(path) || PROMO_FREE_PATHS.some((re) => re.test(path))
}
